import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { WorkspaceRoleService } from "@/services/workspace-role.service";
import type {
  TAccessMapVersion,
  TMemberReachKind,
  TRoleMemberItems,
} from "@/types/role-access-map";
import type { TRoleEligibility } from "@/types/role-eligibility";
import type { ICustomRole } from "@/types/workspace";
import { getQueryClient } from "./client";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, scopeIfReady, type QueryScope } from "./scope";

/**
 * The workspace role reads (NG-2814):
 *
 * - `roles.list`: every role of the workspace, system and custom,
 *   `[slug, uid, 'roles', 'list']`. Mutable: the roles page is where admins
 *   edit them, and the members screens read it for role names and tiers.
 * - `roles.catalogue`: the permission catalogue for one tier,
 *   `[slug, uid, 'roles', 'catalogue', tier]`. Reference: it is the product's
 *   permission list, split into available and locked against the tier, so it
 *   is keyed by tier (one held for the wrong tier would offer a Guest role the
 *   Administrator toggles, NG-1272).
 * - `role.draft`: one role's unpublished snapshot, the role editor's autosave
 *   baseline, `[slug, uid, 'role', id, 'draft']`. Access-sensitive, and read
 *   again on every open of the editor whatever its age (`refetchOnMount:
 *   "always"`), since an editor that starts from an old baseline would
 *   autosave it back over another admin's edit.
 *
 * - `role.eligibility`: who one role's holders may be assigned as, the editor's
 *   "Can be assigned as" section (NG-3896), `[slug, uid, 'role', id,
 *   'eligibility']`. Access-sensitive: it decides who a Lead picker offers.
 *
 * All four are plain GETs, so all declare `"safe-read"`.
 *
 * @writers `WorkspaceRolesStore`: create, duplicate, publish and delete patch
 * the list and then invalidate it, because `is_default` and `can_delete` are
 * verdicts about the whole workspace that one row's answer cannot carry
 * (NG-1335); set-default patches every row from its answer. The draft is
 * written only by its editor, which keeps each autosave's answer as its own
 * baseline and reads the draft again on its next open. The eligibility is
 * written by `useRoleEligibility`'s `save`, which sets the answer it sends at
 * once and then the server's.
 */

/** The roles list key. */
export function workspaceRolesKey(scope: QueryScope) {
  return scopedKey(scope, "roles", "list");
}

/** The permission catalogue key for `tier`. */
function permissionCatalogueKey(scope: QueryScope, tier: string) {
  return scopedKey(scope, "roles", "catalogue", tier);
}

/** One role's draft key. */
function roleDraftKey(scope: QueryScope, roleId: string) {
  return scopedKey(scope, "role", roleId, "draft");
}

/** Every role of the workspace. */
export function workspaceRolesQuery(
  service: Pick<WorkspaceRoleService, "listRoles">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: workspaceRolesKey(scope),
    queryFn: scopedRead((request) => service.listRoles(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/** The permission catalogue for `tier`. */
export function permissionCatalogueQuery(
  service: Pick<WorkspaceRoleService, "getPermissionCatalogue">,
  scope: QueryScope,
  tier: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: permissionCatalogueKey(scope, tier),
    queryFn: scopedRead((request) => service.getPermissionCatalogue(scope.tenant, tier, request), {
      retry: "safe-read",
    }),
  });
}

/** One role's unpublished draft, read when its editor opens. */
export function roleDraftQuery(
  service: Pick<WorkspaceRoleService, "getRoleDraft">,
  scope: QueryScope,
  roleId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    // The class reuses an answer for 60 s (NG-3012); an editor baseline may not
    // be reused at all, and `useRoleDraft` waits for this mount's own read.
    refetchOnMount: "always",
    queryKey: roleDraftKey(scope, roleId),
    queryFn: scopedRead((request) => service.getRoleDraft(scope.tenant, roleId, request), {
      retry: "safe-read",
    }),
  });
}

/** What one role reaches, per version. `revision` is the draft's last save, so an
 *  autosave reads the map again rather than serving the answer from before it. */
/** What one member of a role reaches today, item by item (NG-3939). Keyed on the
 *  member, and fresh as access-sensitive: a membership or role change elsewhere
 *  moves what they reach. */
export function roleMemberReachQuery(
  service: Pick<WorkspaceRoleService, "getRoleMemberReach">,
  scope: QueryScope,
  roleId: string,
  userId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(scope, "role", roleId, "member-reach", userId),
    queryFn: scopedRead(
      (request) => service.getRoleMemberReach(scope.tenant, roleId, userId, request),
      { retry: "safe-read" },
    ),
  });
}

/** How many items a page of one kind holds: a few to start, more for a search. */
const MEMBER_ITEMS_PAGE = 5;
const MEMBER_ITEMS_SEARCH_PAGE = 20;

/** One kind of item a member of a role reaches, a page at a time by name, searched
 *  by `q` (NG-3939). "Show more" reads the next page. */
export function roleMemberItemsQuery(
  service: Pick<WorkspaceRoleService, "getRoleMemberItems">,
  scope: QueryScope,
  roleId: string,
  userId: string,
  kind: TMemberReachKind,
  q: string,
) {
  const limit = q ? MEMBER_ITEMS_SEARCH_PAGE : MEMBER_ITEMS_PAGE;
  return infiniteQueryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(scope, "role", roleId, "member-items", userId, kind, q),
    initialPageParam: 0,
    queryFn: scopedRead<TRoleMemberItems, number>(
      (request, { pageParam }) =>
        service.getRoleMemberItems(
          scope.tenant,
          roleId,
          userId,
          { kind, q, offset: pageParam, limit },
          request,
        ),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: TRoleMemberItems) => last.next_offset ?? undefined,
  });
}

export function roleAccessMapQuery(
  service: Pick<WorkspaceRoleService, "getRoleAccessMap">,
  scope: QueryScope,
  roleId: string,
  version: TAccessMapVersion,
  revision: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(scope, "role", roleId, "access-map", version, revision),
    queryFn: scopedRead(
      (request) => service.getRoleAccessMap(scope.tenant, roleId, version, request),
      { retry: "safe-read" },
    ),
  });
}

/** One role's eligibility key. */
export function roleEligibilityKey(scope: QueryScope, roleId: string) {
  return scopedKey(scope, "role", roleId, "eligibility");
}

/** Who one role's holders may be assigned as (NG-3896). */
export function roleEligibilityQuery(
  service: Pick<WorkspaceRoleService, "getRoleEligibility">,
  scope: QueryScope,
  roleId: string,
) {
  return queryOptions<TRoleEligibility>({
    ...FRESHNESS["access-sensitive"],
    queryKey: roleEligibilityKey(scope, roleId),
    queryFn: scopedRead((request) => service.getRoleEligibility(scope.tenant, roleId, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * The roles list for a route loader: a fresh entry answers with no request,
 * and the answer lands where every `useWorkspaceRoles` reader sees it. Before
 * the session resolves it reads straight from the service, uncached.
 */
export function readWorkspaceRoles(
  service: Pick<WorkspaceRoleService, "listRoles">,
  slug: string,
): Promise<ICustomRole[]> {
  const scope = scopeIfReady(slug, "workspace roles");
  if (!scope) return service.listRoles(slug);
  return getQueryClient().fetchQuery(workspaceRolesQuery(service, scope));
}
